import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { SERVICES, COMPANY } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/hooks/useSeo";

export default function AreaDetail() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setArea(null); setNotFound(false);
    api.get(`/areas/${slug}`).then((r) => setArea(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const prestonSeo = slug === "preston" ? {
    title: "Exterior Cleaning Preston | Roof, Render & Driveway Cleaning | StoneCrest",
    description: "Professional exterior cleaning in Preston, including roof cleaning, render softwashing, driveway and patio cleaning, gutter clearing and fascia cleaning. Free quotes.",
    canonical: "https://stonecrestexteriors.co.uk/areas/preston",
  } : null;
  useSeo(
    prestonSeo ? prestonSeo.title : area?.title,
    prestonSeo ? prestonSeo.description : area?.meta_description,
    slug ? `https://stonecrestexteriors.co.uk/areas/${slug}` : undefined
  );

  if (notFound) return (
    <div className="px-6 py-40 text-center">
      <h1 className="font-serif text-4xl mb-4">Area not found</h1>
      <Link to="/areas" className="text-[var(--oxblood)] underline">Back to areas</Link>
    </div>
  );
  if (!area) return <div className="py-40 text-center text-[var(--muted)]">Loading…</div>;

  return (
    <div>
      <section className="px-6 md:px-12 max-w-4xl mx-auto pt-16 md:pt-24 pb-10">
        <Link to="/areas" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--oxblood)] transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> All Areas</Link>
        <p className="text-xs uppercase tracking-[0.35em] text-[var(--oxblood)] mb-5">Exterior Cleaning in {area.name}</p>
        <h1 className="font-serif font-light text-5xl md:text-7xl leading-[0.98] tracking-tight" data-testid="area-title">{area.name}, Lancashire.</h1>
        <p className="mt-7 text-[var(--muted)] text-lg leading-relaxed">{area.intro}</p>
      </section>

      <section className="px-6 md:px-12 max-w-4xl mx-auto pb-16">
        <div className="prose-none space-y-5 text-[var(--muted)] leading-relaxed text-lg">
          {area.content.split("\n\n").map((p, i) => <Reveal key={i} delay={i * 0.04}><p>{p}</p></Reveal>)}
        </div>
      </section>

      <section className="bg-[var(--bone)]">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-20 md:py-24">
          <h2 className="font-serif font-light text-3xl md:text-4xl mb-8">Our services in {area.name}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors group" data-testid={`area-service-${s.slug}`}>
                <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" /> <span className="group-hover:translate-x-1 transition-transform">{s.name}</span>
              </Link>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/quote" className="bg-[var(--oxblood)] text-white px-8 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors text-center">Free {area.name} Quote</Link>
            <a href={COMPANY.phoneHref} className="border border-[var(--ink)] px-8 py-4 rounded-full tracking-wide hover:bg-[var(--ink)] hover:text-white transition-all flex items-center justify-center gap-2"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
          </div>
          <Link to={`/blog/exterior-cleaning-${area.slug}`} className="inline-block mt-6 text-sm border-b border-[var(--ink)] pb-1 hover:text-[var(--oxblood)] hover:border-[var(--oxblood)] transition-colors" data-testid="area-guide-link">Read our {area.name} exterior cleaning guide →</Link>
        </div>
      </section>

      <CTASection title={`Transform your ${area.name} home.`} />
    </div>
  );
}
